import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  onSnapshot,
  addDoc,
  orderBy,
  limit,
} from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const analytics = getAnalytics(app);
const googleProvider = new GoogleAuthProvider();

// ─── Auth Functions ────────────────────────────────────────────────────────

export const signUpWithEmail = (email, password) =>
  createUserWithEmailAndPassword(auth, email, password);

export const signInWithEmail = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);

export const signInWithGoogle = () =>
  signInWithPopup(auth, googleProvider);

export const logOut = () => signOut(auth);

export const resetPassword = (email) =>
  sendPasswordResetEmail(auth, email);

export const onAuthChange = (callback) =>
  onAuthStateChanged(auth, callback);

// ─── Firestore Schema ──────────────────────────────────────────────────────
//
// /users/{uid}
//   role: 'parent' | 'student'
//   email: string
//   displayName: string
//   familyId: string | null
//   createdAt: timestamp
//
// /families/{familyId}
//   parentId: string (uid)
//   studentId: string | null (uid, set when student accepts invite)
//   studentName: string  ← drives "[Name]'s Wallet" throughout UI
//   inviteToken: string  ← used in invite link
//   inviteEmail: string  ← student email parent entered
//   inviteAccepted: boolean
//   allowance: {
//     amount: number
//     frequency: 'weekly' | 'biweekly' | 'monthly'
//     startDate: timestamp
//   }
//   match: {
//     enabled: boolean
//     type: 'percentage' | 'fixed'
//     value: number        ← % or $ amount
//     monthlyCap: number   ← max match per month, 0 = no cap
//   }
//   subscription: {
//     stripeCustomerId: string
//     stripeSubscriptionId: string
//     status: 'trialing' | 'active' | 'past_due' | 'cancelled'
//     trialEndsAt: timestamp
//   }
//   createdAt: timestamp
//
// /families/{familyId}/weeks/{weekId}   weekId = 'YYYY-WW' (ISO week)
//   startDate: timestamp
//   endDate: timestamp
//   allowanceAmount: number
//   spent: number
//   saved: number
//   matchEarned: number
//   closedOut: boolean
//   closedOutAt: timestamp | null
//
// /families/{familyId}/transactions/{txId}
//   weekId: string
//   amount: number
//   category: string
//   note: string
//   createdAt: timestamp
//   createdBy: uid

// ─── User Functions ────────────────────────────────────────────────────────

export const createUserDoc = async (uid, data) => {
  await setDoc(doc(db, 'users', uid), {
    ...data,
    familyId: null,
    createdAt: serverTimestamp(),
  });
};

export const getUserDoc = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateUserDoc = async (uid, data) => {
  await updateDoc(doc(db, 'users', uid), data);
};

export const subscribeToUser = (uid, callback) =>
  onSnapshot(doc(db, 'users', uid), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });

// ─── Family Functions ──────────────────────────────────────────────────────

export const createFamily = async (parentId, data) => {
  const inviteToken = generateToken();
  const familyRef = await addDoc(collection(db, 'families'), {
    parentId,
    studentId: null,
    studentName: data.studentName,
    inviteToken,
    inviteEmail: data.inviteEmail,
    inviteAccepted: false,
    allowance: data.allowance,
    match: data.match,
    startOnSunday: data.startOnSunday ?? false,
    walletStarted: data.walletStarted ?? true,
    subscription: {
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      status: 'trialing',
      trialEndsAt: null,
    },
    createdAt: serverTimestamp(),
  });

  // Link family to parent user doc
  await updateUserDoc(parentId, { familyId: familyRef.id });

  return { familyId: familyRef.id, inviteToken };
};

export const getFamilyDoc = async (familyId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateFamilyDoc = async (familyId, data) => {
  await updateDoc(doc(db, 'families', familyId), data);
};

export const subscribeToFamily = (familyId, callback) =>
  onSnapshot(doc(db, 'families', familyId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });

export const getFamilyByInviteToken = async (token) => {
  const q = query(
    collection(db, 'families'),
    where('inviteToken', '==', token)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  const data = docSnap.data();
  // Check inviteAccepted in code rather than as a query filter
  // to avoid needing a composite Firestore index
  if (data.inviteAccepted) return null;
  return { id: docSnap.id, ...data };
};

export const acceptInvite = async (familyId, studentUid) => {
  await updateFamilyDoc(familyId, {
    studentId: studentUid,
    inviteAccepted: true,
  });
  await updateUserDoc(studentUid, { familyId });
};

export const updateFamilySettings = async (familyId, { allowance, match }) => {
  await updateFamilyDoc(familyId, { allowance, match });
};

// ─── Week Functions ────────────────────────────────────────────────────────

// ─── Week boundary helper ──────────────────────────────────────────────────
// Week runs Monday–Sunday. Sunday (getDay()===0) is the LAST day of the week,
// so we treat it as day 7 when calculating the Monday anchor.
const getMondayAnchor = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon ... 6=Sat
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sun=6 days after Mon
  return new Date(year, month, day - daysFromMonday); // midnight local Monday
};

// Number of weekly (Mon–Sun) close-outs remaining on or before a target date, counted
// from the current week — i.e. how many times the student can still save before the
// deadline. NOT a rolling 7-day count from "now", which overcounts by one whenever the
// target falls mid-week (89 days → a phantom 13th week that closes AFTER the deadline).
export const weeksUntilTarget = (targetDate, from = new Date()) => {
  if (!targetDate) return null;
  const MS_WEEK = 7 * 24 * 60 * 60 * 1000;
  const dow = from.getDay();                      // 0=Sun … 6=Sat
  const daysToWeekEnd = dow === 0 ? 0 : 7 - dow;  // forward to this week's ending Sunday
  const weekEnd = new Date(from.getFullYear(), from.getMonth(), from.getDate() + daysToWeekEnd);
  const target = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  return Math.max(1, Math.floor((target - weekEnd) / MS_WEEK) + 1);
};

export const getCurrentWeekId = () => {
  const now = new Date();
  const monday = getMondayAnchor(now);
  const startOfYear = new Date(monday.getFullYear(), 0, 1);
  const weekNum = Math.ceil(
    ((monday - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7
  );
  return `${monday.getFullYear()}-${String(weekNum).padStart(2, '0')}`;
};

// Returns week IDs for the past N weeks (not including current)
export const getPastWeekIds = (count = 3) => {
  const ids = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const past = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i * 7);
    const monday = getMondayAnchor(past);
    const startOfYear = new Date(monday.getFullYear(), 0, 1);
    const weekNum = Math.ceil(
      ((monday - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7
    );
    ids.push(`${monday.getFullYear()}-${String(weekNum).padStart(2, '0')}`);
  }
  return ids;
};

// Check if any of the past N weeks are unclosed — returns the oldest unclosed week doc or null
export const getUnclosedPreviousWeek = async (familyId) => {
  const pastIds = getPastWeekIds(3);

  for (const weekId of pastIds) {
    const weekSnap = await getDoc(doc(db, 'families', familyId, 'weeks', weekId));

    if (weekSnap.exists()) {
      // Week doc exists — check if it's closed
      const data = weekSnap.data();
      if (!data.closedOut) return { id: weekSnap.id, ...data };
    } else {
      // No week doc — check if any transactions exist for this period
      // If so, the student has activity that was never closed out
      const txQuery = query(
        collection(db, 'families', familyId, 'transactions'),
        where('weekId', '==', weekId),
        limit(1)
      );
      const txSnap = await getDocs(txQuery);
      if (!txSnap.empty) {
        // Transactions exist for this prior week but no week doc — needs close-out
        return { id: weekId, closedOut: false, allowanceAmount: 0 };
      }
    }
  }

  return null;
};

// ─── Backlog Detection & Clear ─────────────────────────────────────────────
//
// Used when student hasn't closed out 2+ prior weeks.
// Strategy:
//   1. Look back up to 8 weeks (beyond the normal 3-week window).
//   2. Find all unclosed week docs (or weeks with transactions but no doc).
//   3. If 2+ unclosed weeks found, return them so the UI can prompt.
//   4. On confirm, chain carryover correctly from the last properly closed week,
//      mark each unclosed week closed with $0 saved / computed carryover,
//      and write a parent notification.

// Returns array of unclosed prior week IDs (not including current week).
// A week "counts" if it has a week doc that isn't closed, or if it has
// transactions but no week doc at all.
export const detectBacklog = async (familyId) => {
  const unclosed = [];
  const now = new Date();

  for (let i = 1; i <= 8; i++) {
    const past = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i * 7);
    const monday = getMondayAnchor(past);
    const startOfYear = new Date(monday.getFullYear(), 0, 1);
    const weekNum = Math.ceil(
      ((monday - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7
    );
    const weekId = `${monday.getFullYear()}-${String(weekNum).padStart(2, '0')}`;
    const weekSnap = await getDoc(doc(db, 'families', familyId, 'weeks', weekId));

    if (weekSnap.exists()) {
      const data = weekSnap.data();
      if (!data.closedOut) {
        unclosed.push({ weekId, startDate: monday, hasDoc: true, data });
      } else {
        // Hit a properly closed week — stop looking further back
        break;
      }
    } else {
      // No week doc — check for orphaned transactions
      const txQuery = query(
        collection(db, 'families', familyId, 'transactions'),
        where('weekId', '==', weekId),
        limit(1)
      );
      const txSnap = await getDocs(txQuery);
      if (!txSnap.empty) {
        unclosed.push({ weekId, startDate: monday, hasDoc: false, data: null });
      }
      // No doc and no transactions → week simply never started; stop looking back
      else {
        break;
      }
    }
  }

  // Return oldest first so we can chain carryover forward
  return unclosed.reverse();
};

// Clears the backlog by marking all unclosed prior weeks as closed.
// Chains carryover correctly: finds the last properly closed week,
// then propagates carryover week-by-week through the empty weeks.
// Writes a parent notification for visibility.
export const clearBacklog = async (familyId, baseAllowance, unclosedWeeks, parentId) => {
  if (!unclosedWeeks.length) return;

  // Find the carryover from the last properly closed week
  let runningCarryover = 0;
  try {
    const prevWeeksQuery = query(
      collection(db, 'families', familyId, 'weeks'),
      where('closedOut', '==', true),
      orderBy('startDate', 'desc'),
      limit(1)
    );
    const prevSnap = await getDocs(prevWeeksQuery);
    if (!prevSnap.empty) {
      const prevWeek = prevSnap.docs[0].data();
      runningCarryover = prevWeek.carryover !== undefined
        ? prevWeek.carryover
        : (prevWeek.allowanceAmount || 0) - (prevWeek.spent || 0) - (prevWeek.saved || 0);
    }
  } catch (err) {
    console.log('No prior closed week found; starting carryover at 0');
  }

  // Process each unclosed week oldest → newest, chaining carryover
  for (const { weekId, startDate, hasDoc } of unclosedWeeks) {
    const effectiveAllowance = Math.round((baseAllowance + runningCarryover) * 100) / 100;

    // For weeks with no doc, create a minimal one before closing
    if (!hasDoc) {
      const endOfWeek = new Date(
        startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6
      );
      await setDoc(doc(db, 'families', familyId, 'weeks', weekId), {
        startDate,
        endDate: endOfWeek,
        baseAllowance,
        carryoverFromPrev: runningCarryover,
        allowanceAmount: effectiveAllowance,
        spent: 0,
        plannedSpend: 0,
        bonusIncome: 0,
        saved: 0,
        matchEarned: 0,
        closedOut: false,
        closedOutAt: null,
        carryover: 0,
        clearedAsBacklog: true,
      });
    }

    // Mark closed: $0 saved, $0 match, carryover = full effective allowance (nothing was saved/spent)
    // (If week has real transactions, they stay in Firestore but savings locked at 0)
    const weekCarryover = hasDoc
      ? Math.round((effectiveAllowance - (await getDoc(
          doc(db, 'families', familyId, 'weeks', weekId)
        )).data().spent || 0) * 100) / 100
      : effectiveAllowance;

    await updateDoc(doc(db, 'families', familyId, 'weeks', weekId), {
      saved: 0,
      matchEarned: 0,
      carryover: weekCarryover,
      closedOut: true,
      closedOutAt: serverTimestamp(),
      clearedAsBacklog: true,
    });

    runningCarryover = weekCarryover;
  }

  // Notify parent — write to a subcollection they can subscribe to
  try {
    await addDoc(collection(db, 'families', familyId, 'notifications'), {
      type: 'backlog_cleared',
      weeksCleared: unclosedWeeks.length,
      weekIds: unclosedWeeks.map(w => w.weekId),
      clearedAt: serverTimestamp(),
      message: `${unclosedWeeks.length} unclosed ${unclosedWeeks.length === 1 ? 'week was' : 'weeks were'} marked closed with $0 saved. Carryover has been recalculated.`,
    });
  } catch (err) {
    console.log('Could not write parent notification:', err);
  }
};

// Get transactions for any week by ID (for prior week close-out)
export const getWeekTransactions = async (familyId, weekId) => {
  const q = query(
    collection(db, 'families', familyId, 'transactions'),
    where('weekId', '==', weekId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getOrCreateCurrentWeek = async (familyId, allowanceAmount, familyData) => {
  // If the family is waiting for Sunday to start, don't create a week doc yet
  if (familyData?.startOnSunday && !familyData?.walletStarted) return null;

  const weekId = getCurrentWeekId();
  const weekRef = doc(db, 'families', familyId, 'weeks', weekId);
  const snap = await getDoc(weekRef);

  if (snap.exists()) {
    const existing = { id: snap.id, ...snap.data() };
    // If week exists with $0 carryover, check if the immediately prior week
    // has since been closed and we should now apply its carryover
    if (existing.carryoverFromPrev === 0) {
      try {
        const now = new Date();
        const thisMonday = getMondayAnchor(now);
        const prevMonday = new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 7);
        const prevStartOfYear = new Date(prevMonday.getFullYear(), 0, 1);
        const prevWeekNum = Math.ceil(((prevMonday - prevStartOfYear) / 86400000 + prevStartOfYear.getDay() + 1) / 7);
        const prevWeekId = `${prevMonday.getFullYear()}-${String(prevWeekNum).padStart(2, '0')}`;

        if (prevWeekId !== weekId) {
          const prevWeekSnap = await getDoc(doc(db, 'families', familyId, 'weeks', prevWeekId));
          if (prevWeekSnap.exists()) {
            const prevWeek = prevWeekSnap.data();
            if (prevWeek.closedOut && prevWeek.carryover !== undefined && prevWeek.carryover !== 0) {
              const carryover = prevWeek.carryover;
              const effectiveAllowance = Math.round((allowanceAmount + carryover) * 100) / 100;
              await updateDoc(weekRef, {
                carryoverFromPrev: carryover,
                allowanceAmount: effectiveAllowance,
              });
              return { ...existing, carryoverFromPrev: carryover, allowanceAmount: effectiveAllowance };
            }
          }
        }
      } catch (err) {
        console.log('Could not apply carryover to existing week:', err);
      }
    }
    return existing;
  }

  // Only use carryover from the immediately prior week, and only if it's closed.
  // If the prior week is still open, carryover = 0 until it closes.
  let carryover = 0;
  try {
    const now = new Date();
    const thisMonday = getMondayAnchor(now);
    const prevMonday = new Date(thisMonday.getFullYear(), thisMonday.getMonth(), thisMonday.getDate() - 7);
    const prevStartOfYear = new Date(prevMonday.getFullYear(), 0, 1);
    const prevWeekNum = Math.ceil(((prevMonday - prevStartOfYear) / 86400000 + prevStartOfYear.getDay() + 1) / 7);
    const prevWeekId = `${prevMonday.getFullYear()}-${String(prevWeekNum).padStart(2, '0')}`;

    const prevWeekSnap = await getDoc(doc(db, 'families', familyId, 'weeks', prevWeekId));
    if (prevWeekSnap.exists()) {
      const prevWeek = prevWeekSnap.data();
      if (prevWeek.closedOut) {
        // Prior week is closed — use its carryover
        carryover = prevWeek.carryover !== undefined
          ? prevWeek.carryover
          : (prevWeek.allowanceAmount || 0) - (prevWeek.spent || 0) - (prevWeek.saved || 0);
      } else {
        // Prior week not yet closed — start with $0 carryover
        // Will be updated when prior week closes via closeOutWeek
        carryover = 0;
      }
    }
    // If no prior week doc exists at all, carryover stays 0
  } catch (err) {
    console.log('Could not determine prior week carryover, starting at 0:', err);
  }

  // New week budget = base allowance + carryover (can be negative if overspent)
  const effectiveAllowance = Math.round((allowanceAmount + carryover) * 100) / 100;

  // Create new week using Monday anchor to avoid UTC boundary bug
  const now = new Date();
  const startOfWeek = getMondayAnchor(now); // Monday
  const endOfWeek = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + 6); // Sunday

  const newWeek = {
    startDate: startOfWeek,
    endDate: endOfWeek,
    baseAllowance: allowanceAmount,      // always the parent-set weekly amount
    carryoverFromPrev: carryover,        // what rolled in from last week (+/-)
    allowanceAmount: effectiveAllowance, // what the student actually has to work with
    spent: 0,
    plannedSpend: 0,
    bonusIncome: 0,
    saved: 0,
    matchEarned: 0,
    closedOut: false,
    closedOutAt: null,
    carryover: 0,                        // will be set on close-out
  };

  await setDoc(weekRef, newWeek);

  // Recalculate totals in case planned transactions were rolled over before this week doc was created
  const existingTxSnap = await getDocs(
    query(collection(db, 'families', familyId, 'transactions'), where('weekId', '==', weekId))
  );
  if (!existingTxSnap.empty) {
    const existingTxs = existingTxSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(t => t.type !== 'deleted' && t.type !== 'converted');
    await recalculateWeekTotals(familyId, weekId, existingTxs);
  }

  return { id: weekId, ...newWeek };
};

// Returns the week ID for 7 days from now (next Monday–Sunday period)
export const getNextWeekId = () => {
  const now = new Date();
  const nextWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
  const monday = getMondayAnchor(nextWeek);
  const startOfYear = new Date(monday.getFullYear(), 0, 1);
  const weekNum = Math.ceil(
    ((monday - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7
  );
  return `${monday.getFullYear()}-${String(weekNum).padStart(2, '0')}`;
};

// Ensures a week doc exists for next week — creates a minimal stub if not.
// Does NOT apply carryover (that happens at actual close-out time).
export const getOrCreateNextWeek = async (familyId, allowanceAmount) => {
  const weekId = getNextWeekId();
  const weekRef = doc(db, 'families', familyId, 'weeks', weekId);
  const snap = await getDoc(weekRef);
  if (snap.exists()) return { id: snap.id, ...snap.data() };

  const now = new Date();
  const nextWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
  const startOfNextWeek = getMondayAnchor(nextWeek); // Monday
  const endOfNextWeek = new Date(startOfNextWeek.getFullYear(), startOfNextWeek.getMonth(), startOfNextWeek.getDate() + 6); // Sunday

  const newWeek = {
    startDate: startOfNextWeek,
    endDate: endOfNextWeek,
    baseAllowance: allowanceAmount,
    carryoverFromPrev: 0,         // will be recalculated at actual close-out
    allowanceAmount: allowanceAmount,
    spent: 0,
    plannedSpend: 0,
    bonusIncome: 0,
    saved: 0,
    matchEarned: 0,
    closedOut: false,
    closedOutAt: null,
    carryover: 0,
  };

  await setDoc(weekRef, newWeek);
  return { id: weekId, ...newWeek };
};

export const subscribeToCurrentWeek = (familyId, callback) => {
  const weekId = getCurrentWeekId();
  return onSnapshot(
    doc(db, 'families', familyId, 'weeks', weekId),
    (snap) => {
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    }
  );
};

export const subscribeToWeeks = (familyId, callback) => {
  const q = query(
    collection(db, 'families', familyId, 'weeks'),
    orderBy('startDate', 'desc'),
    limit(12)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const closeOutWeek = async (familyId, weekId, { saved, matchEarned, carryover, goalAllocation = 0, goalName = '' }) => {
  await updateDoc(doc(db, 'families', familyId, 'weeks', weekId), {
    saved,
    matchEarned,
    carryover,
    goalAllocation,
    goalName: goalAllocation > 0 ? goalName : '',
    closedOut: true,
    closedOutAt: serverTimestamp(),
  });

  // After closing out, update the next week's carryover if that week doc already exists
  // This handles the case where the next week was created before this week was closed
  try {
    const closedSnap = await getDoc(doc(db, 'families', familyId, 'weeks', weekId));
    const closedWeek = closedSnap.data();
    const closedStart = closedWeek.startDate?.toDate
      ? closedWeek.startDate.toDate()
      : new Date(closedWeek.startDate);
    const nextMonday = new Date(closedStart.getFullYear(), closedStart.getMonth(), closedStart.getDate() + 7);
    const nextStartOfYear = new Date(nextMonday.getFullYear(), 0, 1);
    const nextWeekNum = Math.ceil(((nextMonday - nextStartOfYear) / 86400000 + nextStartOfYear.getDay() + 1) / 7);
    const nextWeekId = `${nextMonday.getFullYear()}-${String(nextWeekNum).padStart(2, '0')}`;

    const nextWeekRef = doc(db, 'families', familyId, 'weeks', nextWeekId);
    const nextSnap = await getDoc(nextWeekRef);
    if (nextSnap.exists()) {
      const nextWeek = nextSnap.data();
      // Only update if the next week hasn't been closed yet and has $0 carryover
      if (!nextWeek.closedOut && nextWeek.carryoverFromPrev === 0 && carryover !== 0) {
        const baseAllowance = nextWeek.baseAllowance || 0;
        const effectiveAllowance = Math.round((baseAllowance + carryover) * 100) / 100;
        await updateDoc(nextWeekRef, {
          carryoverFromPrev: carryover,
          allowanceAmount: effectiveAllowance,
        });
      }
    }
  } catch (err) {
    console.log('Could not update next week carryover after close-out:', err);
  }
};

// ─── Savings Goal Functions ────────────────────────────────────────────────

export const getActiveGoal = async (familyId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return data.activeGoal?.status === 'active' ? data.activeGoal : null;
};

export const subscribeToActiveGoal = (familyId, callback) =>
  onSnapshot(doc(db, 'families', familyId), (snap) => {
    if (!snap.exists()) { callback(null); return; }
    const data = snap.data();
    callback(data.activeGoal?.status === 'active' ? data.activeGoal : null);
  });

export const setActiveGoal = async (familyId, { name, targetAmount, targetDate, weeklyTarget, initialAllocation = 0 }) => {
  const goal = {
    name,
    targetAmount,
    targetDate,
    originalTargetDate: targetDate,
    weeklyTarget,
    amountSaved: initialAllocation,
    createdAt: serverTimestamp(),
    completedAt: null,
    status: 'active',
  };
  await updateFamilyDoc(familyId, { activeGoal: goal });
  return goal;
};

// Called after each close-out to update goal progress and recalculate target date
// Update an existing active goal — preserves amountSaved, recalculates from remaining
export const updateActiveGoal = async (familyId, { name, targetAmount, targetDate, weeklyTarget, additionalAllocation }) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return;
  const { activeGoal } = snap.data();
  if (!activeGoal || activeGoal.status !== 'active') return;

  const updatedGoal = {
    ...activeGoal,
    name,
    targetAmount,
    targetDate,
    weeklyTarget,
    // preserve amountSaved, originalTargetDate, createdAt, status
  };
  await updateFamilyDoc(familyId, { activeGoal: updatedGoal });

  // If the student allocated additional savings toward the goal, apply them now.
  // Delegates to applyGoalAllocation so completion detection and notifications
  // are handled consistently with the weekly close-out flow.
  if (additionalAllocation && additionalAllocation > 0) {
    return await applyGoalAllocation(familyId, additionalAllocation);
  }

  return updatedGoal;
};

export const applyGoalAllocation = async (familyId, goalAllocation, weekId = null) => {
  if (!goalAllocation || goalAllocation <= 0) return null;

  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return null;
  const { activeGoal } = snap.data();
  if (!activeGoal || activeGoal.status !== 'active') return null;

  // Stamp the goal name on the week doc for permanent chip labeling
  if (weekId) {
    try {
      await updateDoc(doc(db, 'families', familyId, 'weeks', weekId), {
        goalName: activeGoal.name,
      });
    } catch (err) { console.log('Could not stamp goalName on week:', err); }
  }

  const newAmountSaved = Math.round((activeGoal.amountSaved + goalAllocation) * 100) / 100;
  const isComplete = newAmountSaved >= activeGoal.targetAmount;

  if (isComplete) {
    const completedGoal = { ...activeGoal, amountSaved: newAmountSaved,
      status: 'completed', completedAt: new Date() };
    const goalHistory = snap.data().goalHistory || [];
    await updateFamilyDoc(familyId, {
      activeGoal: completedGoal,
      goalHistory: [...goalHistory, completedGoal],
    });
    try {
      await addDoc(collection(db, 'families', familyId, 'notifications'), {
        type: 'goal_completed',
        goalName: activeGoal.name,
        targetAmount: activeGoal.targetAmount,
        completedAt: serverTimestamp(),
        message: `${activeGoal.name} goal completed! ${formatCurrency(activeGoal.targetAmount)} saved.`,
      });
    } catch (err) { console.log('Could not write goal completion notification:', err); }
    return { ...completedGoal, justCompleted: true };
  }

  // Recalculate the weekly target from the student's CURRENT target date.
  // Important: we anchor off activeGoal.targetDate (what the student currently wants),
  // NOT originalTargetDate. The original date is preserved only as a historical record;
  // using it here would silently undo any deliberate change the student made to their
  // deadline (e.g. moving the goal earlier), which is what caused the date to "revert".
  const remaining = Math.round((activeGoal.targetAmount - newAmountSaved) * 100) / 100;

  const currentTargetDate = activeGoal.targetDate?.toDate
    ? activeGoal.targetDate.toDate()
    : activeGoal.targetDate?.seconds
      ? new Date(activeGoal.targetDate.seconds * 1000)
      : null;

  let updatedGoal;
  if (currentTargetDate && remaining > 0) {
    // Weekly close-outs remaining on/before the student's chosen target date.
    const weeksLeft = weeksUntilTarget(currentTargetDate);
    const newWeeklyTarget = Math.round(Math.ceil((remaining / weeksLeft) * 100) / 100 * 100) / 100;

    // Keep the student's chosen target date. Only recompute the weekly amount to reflect
    // the new (lower) remaining balance after this allocation. The date does NOT move.
    updatedGoal = {
      ...activeGoal,
      amountSaved: newAmountSaved,
      weeklyTarget: newWeeklyTarget,
      // targetDate intentionally unchanged — preserve the student's deadline
    };
  } else {
    // No usable target date, or goal effectively met — just record the new amount.
    updatedGoal = {
      ...activeGoal,
      amountSaved: newAmountSaved,
    };
  }

  await updateFamilyDoc(familyId, { activeGoal: updatedGoal });
  return updatedGoal;
};

export const subscribeToGoalHistory = (familyId, callback) =>
  onSnapshot(doc(db, 'families', familyId), (snap) => {
    if (!snap.exists()) { callback([]); return; }
    callback(snap.data().goalHistory || []);
  });

// Mark a completed goal as purchased — student confirmed they spent the money.
// Sets purchasedAt and records spentFromSavings so the hero card can net it out.
export const markGoalPurchased = async (familyId, goalName) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return;
  const { goalHistory = [], activeGoal } = snap.data();

  // Find the most recent completed entry matching this goal name
  const updatedHistory = goalHistory.map((g, i) => {
    if (g.name === goalName && g.status === 'completed' && !g.purchasedAt) {
      // Only update the most recent match
      const isLatest = !goalHistory.slice(i + 1).some(
        h => h.name === goalName && h.status === 'completed' && !h.purchasedAt
      );
      if (isLatest) return { ...g, purchasedAt: new Date(), spentFromSavings: true };
    }
    return g;
  });

  const updates = { goalHistory: updatedHistory };

  // If activeGoal is this completed goal, mark it purchased there too
  if (activeGoal?.name === goalName && activeGoal?.status === 'completed') {
    updates.activeGoal = { ...activeGoal, purchasedAt: new Date(), spentFromSavings: true };
  }

  await updateFamilyDoc(familyId, updates);
};

export const cancelGoal = async (familyId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return;
  const { activeGoal, goalHistory: existingHistory = [] } = snap.data();
  if (!activeGoal) return;

  // Calculate total that had been allocated toward this goal across all weeks
  let totalAllocated = 0;
  try {
    const weeksSnap = await getDocs(
      query(collection(db, 'families', familyId, 'weeks'),
        where('goalAllocation', '>', 0))
    );
    const weekUpdates = weeksSnap.docs.map(async (weekDoc) => {
      const data = weekDoc.data();
      // Only clear allocations for this specific goal
      if (!data.goalName || data.goalName === activeGoal.name) {
        totalAllocated += data.goalAllocation || 0;
        await updateDoc(weekDoc.ref, { goalAllocation: 0, goalName: '' });
      }
    });
    await Promise.all(weekUpdates);
    totalAllocated = Math.round(totalAllocated * 100) / 100;
  } catch (err) {
    console.log('Could not clear week goalAllocations:', err);
  }

  await updateFamilyDoc(familyId, {
    activeGoal: { ...activeGoal, status: 'cancelled' },
    goalHistory: [...existingHistory, {
      ...activeGoal,
      status: 'cancelled',
      cancelledAt: new Date(),
      totalAllocated, // audit trail — how much had been saved toward this goal
    }],
  });
};

// Recalculate totals for any week (not just current) — used during prior-week close-out
export const recalculateWeekTotalsById = async (familyId, weekId, transactions) => {
  const spent = transactions
    .filter(t => (t.type === 'actual' || !t.type) && t.type !== 'converted' && t.type !== 'deleted')
    .reduce((s, t) => s + t.amount, 0);
  const plannedSpend = transactions
    .filter(t => t.type === 'planned')
    .reduce((s, t) => s + t.amount, 0);
  const bonusIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((s, t) => s + t.amount, 0);
  await updateDoc(doc(db, 'families', familyId, 'weeks', weekId), {
    spent: Math.round(spent * 100) / 100,
    plannedSpend: Math.round(plannedSpend * 100) / 100,
    bonusIncome: Math.round(bonusIncome * 100) / 100,
  });
};

export const updateWeekSpent = async (familyId, weekId, spent) => {
  await updateDoc(doc(db, 'families', familyId, 'weeks', weekId), { spent });
};

// Updates week totals after any transaction add
// Recalculates from the full transaction list to stay accurate
export const recalculateWeekTotals = async (familyId, weekId, transactions) => {
  const spent = transactions
    .filter(t => (t.type === 'actual' || !t.type) && t.type !== 'converted')
    .reduce((s, t) => s + t.amount, 0);
  const plannedSpend = transactions
    .filter(t => t.type === 'planned')
    .reduce((s, t) => s + t.amount, 0);
  const bonusIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((s, t) => s + t.amount, 0);

  await updateDoc(doc(db, 'families', familyId, 'weeks', weekId), {
    spent: Math.round(spent * 100) / 100,
    plannedSpend: Math.round(plannedSpend * 100) / 100,
    bonusIncome: Math.round(bonusIncome * 100) / 100,
  });
};

// Converts a planned transaction to actual, optionally with an adjusted amount
// Marks the planned entry as 'converted' and creates a new 'actual' entry
export const convertPlannedToActual = async (familyId, weekId, plannedTx, actualAmount, allTransactions, spendDate) => {
  // Guard against duplicate conversion (e.g. double-tap or network retry)
  // Check if an actual transaction already exists for this planned item
  const existingSnap = await getDocs(
    query(
      collection(db, 'families', familyId, 'transactions'),
      where('convertedFromId', '==', plannedTx.id),
      where('type', '==', 'actual'),
      limit(1)
    )
  );
  if (!existingSnap.empty) {
    console.log(`Skipping duplicate conversion for planned tx ${plannedTx.id}`);
    return;
  }

  // Mark the planned entry as converted (removes it from planned totals)
  await updateDoc(doc(db, 'families', familyId, 'transactions', plannedTx.id), {
    type: 'converted',
    convertedAt: serverTimestamp(),
    convertedAmount: actualAmount,
  });

  // Create the new actual transaction
  await addDoc(collection(db, 'families', familyId, 'transactions'), {
    weekId,
    amount: actualAmount,
    category: plannedTx.category,
    note: plannedTx.note,
    type: 'actual',
    spendDate: spendDate !== undefined ? spendDate : new Date().getDay(),
    convertedFromId: plannedTx.id,
    createdAt: serverTimestamp(),
  });

  // Recalculate totals — replace planned tx with converted, add actual
  const updatedTxs = allTransactions
    .map(t => t.id === plannedTx.id ? { ...t, type: 'converted' } : t)
    .concat({ amount: actualAmount, type: 'actual', category: plannedTx.category });

  await recalculateWeekTotals(familyId, weekId, updatedTxs);
};

// ─── Transaction Functions ─────────────────────────────────────────────────


// ─── Recurring Planned Item Templates ─────────────────────────────────────────
// Stored on the family doc as recurringTemplates: [{ id, amount, category, note, spendDay }]
// spendDay is a 0-6 index (0=Sun, 1=Mon...6=Sat) matching the existing spendDate convention.

export const getRecurringTemplates = async (familyId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return [];
  return snap.data().recurringTemplates || [];
};

export const subscribeToRecurringTemplates = (familyId, callback) =>
  onSnapshot(doc(db, 'families', familyId), (snap) => {
    if (!snap.exists()) { callback([]); return; }
    callback(snap.data().recurringTemplates || []);
  });

export const saveRecurringTemplate = async (familyId, template) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return;
  const existing = snap.data().recurringTemplates || [];
  const newTemplate = {
    id: `rt_${Date.now()}`,
    amount: template.amount,
    category: template.category,
    note: template.note || '',
    spendDay: template.spendDay, // 0-6
    createdAt: new Date(),
  };
  await updateFamilyDoc(familyId, {
    recurringTemplates: [...existing, newTemplate],
  });
  return newTemplate;
};

export const deleteRecurringTemplate = async (familyId, templateId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return;
  const existing = snap.data().recurringTemplates || [];
  await updateFamilyDoc(familyId, {
    recurringTemplates: existing.filter(t => t.id !== templateId),
  });
};

// Check if recurring templates have already been added to a given week
// Returns true if any planned transaction was created from a template this week
export const getWeekRecurringApplied = async (familyId, weekId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return false;
  const applied = snap.data().recurringAppliedWeeks || [];
  return applied.includes(weekId);
};

export const markWeekRecurringApplied = async (familyId, weekId) => {
  const snap = await getDoc(doc(db, 'families', familyId));
  if (!snap.exists()) return;
  const applied = snap.data().recurringAppliedWeeks || [];
  if (!applied.includes(weekId)) {
    await updateFamilyDoc(familyId, {
      recurringAppliedWeeks: [...applied.slice(-12), weekId], // keep last 12 weeks max
    });
  }
};

export const addTransaction = async (familyId, transaction) => {
  const ref = await addDoc(
    collection(db, 'families', familyId, 'transactions'),
    {
      ...transaction,
      createdAt: serverTimestamp(),
    }
  );
  return ref.id;
};

export const subscribeToWeekTransactions = (familyId, weekId, callback) => {
  const q = query(
    collection(db, 'families', familyId, 'transactions'),
    where('weekId', '==', weekId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const deleteTransaction = async (familyId, txId, allTransactions) => {
  await updateDoc(doc(db, 'families', familyId, 'transactions', txId), {
    type: 'deleted',
  });
  // Recalc the week the transaction actually belongs to — not always the current week.
  // Deleting a prior-week transaction with getCurrentWeekId here would overwrite the
  // current week's totals with the wrong week's data.
  const existing = allTransactions.find(t => t.id === txId);
  const weekId = existing?.weekId || getCurrentWeekId();
  // Recalculate totals excluding deleted tx
  const remaining = allTransactions.filter(t => t.id !== txId);
  await recalculateWeekTotals(familyId, weekId, remaining);
};

export const updateTransaction = async (familyId, txId, updates, allTransactions) => {
  await updateDoc(doc(db, 'families', familyId, 'transactions', txId), updates);
  // Recalc the week the transaction actually belongs to — NOT always the current week.
  // Closing out a prior week edits prior-week transactions; using getCurrentWeekId here
  // would overwrite the current week's totals with the wrong week's data.
  const existing = allTransactions.find(t => t.id === txId);
  const sourceWeekId = existing?.weekId || getCurrentWeekId();
  const isMove = updates.weekId && updates.weekId !== sourceWeekId;
  // Source week: its transactions with this update applied, minus the tx if it moved away
  const sourceTxs = allTransactions
    .map(t => (t.id === txId ? { ...t, ...updates } : t))
    .filter(t => !(isMove && t.id === txId));
  await recalculateWeekTotals(familyId, sourceWeekId, sourceTxs);
  // If the transaction moved to a different week, recalc that week from fresh data too
  if (isMove) {
    const targetTxs = await getWeekTransactions(familyId, updates.weekId);
    await recalculateWeekTotals(familyId, updates.weekId, targetTxs);
  }
};

export const deleteAccount = async (familyId, parentUid, studentUid) => {
  // Call the Cloud Function to handle server-side deletion cascade
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const functions = getFunctions();
  const deleteFn = httpsCallable(functions, 'deleteAccount');
  await deleteFn({ familyId, parentUid, studentUid });
};

// ─── Utilities ─────────────────────────────────────────────────────────────

const generateToken = () =>
  Math.random().toString(36).substring(2, 10) +
  Math.random().toString(36).substring(2, 10);

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export const calculateMatch = (saved, matchConfig) => {
  if (!matchConfig?.enabled || !saved || saved <= 0) return 0;
  let match = 0;
  if (matchConfig.type === 'percentage') {
    match = (saved * matchConfig.value) / 100;
  } else {
    match = Math.min(saved, matchConfig.value);
  }
  if (matchConfig.monthlyCap > 0) {
    match = Math.min(match, matchConfig.monthlyCap);
  }
  return Math.round(match * 100) / 100;
};
